import {enumToValues} from '@netx/core-next/utils/enum';
import {AttributeTemplateType} from './schema';

/**
 * used to signify we want to empty the attribute's value
 * react-hook-form does not play well with `undefined` values
 * so we use a symbol to signify we want to empty the value
 * since we can no longer differentiate as `null` (emptied) or `undefined` (not set)
 */
export const emptyValueSymbol = Symbol('emptyValue');

export enum AttributePref {
	SEMANTICS_ENABLED = 'metadata.semantic.enabled',
}

export const ATTRIBUTE_ID = 'attributeid';

export enum AttributeType {
	TEXT = 1,
	TEXTAREA = 2,
	SELECT = 3,
	DATE = 4,
	NUMBER = 7,
	HIERARCHICAL = 8,
	SEMANTIC = 12,
	TAG = 13,
}

export const ATTRIBUTE_TYPE_NAME: Record<AttributeTemplateType, string> = {
	[AttributeTemplateType.DATE]: 'date',
	[AttributeTemplateType.HIERARCHICAL]: 'hierarchical',
	[AttributeTemplateType.NUMBER]: 'number',
	[AttributeTemplateType.SELECT]: 'select',
	[AttributeTemplateType.SEMANTIC]: 'semantic',
	[AttributeTemplateType.TAG]: 'tag',
	[AttributeTemplateType.TEXT]: 'text',
	[AttributeTemplateType.TEXTAREA]: 'textarea',
};

export const ATTRIBUTE_ACTION = <const>{
	APPEND: 'append',
	CLEAR: 'clear',
	OVERWRITE: 'overwrite',
	REMOVE: 'remove',
	SET_IF_BLANK: 'set_if_blank',
};

/**
 * Attribute template types that can have values appended to them
 */
export const ATTRIBUTE_APPENDABLE_TYPES: readonly AttributeTemplateType[] = [
	AttributeTemplateType.TEXT,
	AttributeTemplateType.TEXTAREA,
	AttributeTemplateType.SEMANTIC,
	AttributeTemplateType.TAG,
];

/**
 * Attribute template types that can have default values set
 */
export const ATTRIBUTE_DEFAULTABLE_TYPES: readonly AttributeTemplateType[] = [
	AttributeTemplateType.DATE,
	AttributeTemplateType.NUMBER,
	AttributeTemplateType.TEXT,
	AttributeTemplateType.TEXTAREA,
];

// !!! hierarchical is not supported for edit !!!
// !!! I REPEAT: hierarchical is not editable !!!
export const ATTRIBUTE_EDITABLE_TYPES = enumToValues(
	AttributeTemplateType,
).filter((type) => type !== AttributeTemplateType.HIERARCHICAL);

/**
 * Attribute template types that can be used as facets
 */
export const ATTRIBUTE_FACET_TYPES: readonly AttributeTemplateType[] = [
	AttributeTemplateType.SELECT,
	AttributeTemplateType.TAG,
];

/**
 * Attribute template types that can accept multiple values
 */
export const ATTRIBUTE_MULTIPLE_VALUE_TYPES: readonly AttributeTemplateType[] =
	[AttributeTemplateType.SEMANTIC, AttributeTemplateType.TAG];

/**
 * This was from a failed multi value attribute project
 * Not to be confused with attributes that can have multiple values BUT was
 * intended to be multiple instances of the same attribute
 * @deprecated
 */
export const ATTRIBUTE_MULTIPLE_VALUE_DEPRECATED_TYPES: readonly AttributeTemplateType[] =
	[
		AttributeTemplateType.TEXT,
		AttributeTemplateType.TEXTAREA,
		AttributeTemplateType.SELECT,
		AttributeTemplateType.DATE,
		AttributeTemplateType.NUMBER,
	];

export const ATTRIBUTE_NUMBER_SPEC_2_TYPE = <const>{
	WHOLE: 0,
	ONE_DECIMAL: 1,
	TWO_DECIMALS: 2,
	THREE_DECIMALS: 3,
	/** "free," but not negative */
	UNLIMITED: 4,
	/** TODO: what is 'sortable' - should all number not be sortable? */
	SORTABLE: 5,
};

export const ATTRIBUTE_NUMBER_TESTS: Record<
	(typeof ATTRIBUTE_NUMBER_SPEC_2_TYPE)[keyof typeof ATTRIBUTE_NUMBER_SPEC_2_TYPE],
	string
> = {
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.ONE_DECIMAL]: `^(-?\\d+(\\.\\d{0,1})?|-?\\.\\d{1})$`,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.SORTABLE]: `.*`,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.THREE_DECIMALS]: `^(-?\\d+(\\.\\d{0,3})?|-?\\.\\d{1,3})$`,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.TWO_DECIMALS]: `^(-?\\d+(\\.\\d{0,2})?|-?\\.\\d{1,2})$`,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.UNLIMITED]: `^\\d*\\.?\\d+$`,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.WHOLE]: `^-?\\d+$`,
};

export const ATTRIBUTE_NUMBER_MAX_DECIMAL = {
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.ONE_DECIMAL]: 1,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.THREE_DECIMALS]: 3,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.TWO_DECIMALS]: 2,
	[ATTRIBUTE_NUMBER_SPEC_2_TYPE.UNLIMITED]: Infinity,
};

export const ATTRIBUTE_OBJECT_TYPE = <const>{
	ASSET: 'asset',
	CLIP: 'clip',
};

export const SystemAttributeName = {
	ASSET_ID: 'assetId',
	CATEGORY_PATHS: 'categoryPaths',
	CHECKSUM: 'checksum',
	CREATION_DATE: 'creationdate',
	DIMENSIONS: 'dimensions',
	EXPIRE_DATE: 'expireDate',
	FILE: 'file',
	FILE_FORMAT_FAMILY: 'fileFormatFamily',
	FILE_SIZE_2: 'fileSize2',
	FILE_TYPE_LABEL: 'filetypelabel',
	HEIGHT: 'height',
	IMPORT_DATE: 'importDate',
	MOD_DATE: 'moddate',
	MOD_USER: 'modUser',
	NAME: 'name',
	PATH: 'path',
	SMART_LABELS: 'smart labels',
	THUMB_PATH: 'thumbPath',
	WIDTH: 'width',
	IDENTIFIED_PEOPLE: 'identifiedPeople',
} as const;

export type SystemAttributeName =
	(typeof SystemAttributeName)[keyof typeof SystemAttributeName];

export const ATTRIBUTE_SYSTEM = <const>{
	...SystemAttributeName,
};

export const ATTRIBUTE_SYSTEM_ID: Record<
	(typeof ATTRIBUTE_SYSTEM)[keyof typeof ATTRIBUTE_SYSTEM],
	number
> = <const>{
	[ATTRIBUTE_SYSTEM.NAME]: -100,
	[ATTRIBUTE_SYSTEM.FILE]: -101,
	[ATTRIBUTE_SYSTEM.ASSET_ID]: -102,
	[ATTRIBUTE_SYSTEM.FILE_SIZE_2]: -104,
	[ATTRIBUTE_SYSTEM.FILE_TYPE_LABEL]: -105,
	[ATTRIBUTE_SYSTEM.FILE_FORMAT_FAMILY]: -106,
	[ATTRIBUTE_SYSTEM.PATH]: -107,
	[ATTRIBUTE_SYSTEM.CATEGORY_PATHS]: -108,
	[ATTRIBUTE_SYSTEM.EXPIRE_DATE]: -109,
	[ATTRIBUTE_SYSTEM.MOD_DATE]: -110,
	[ATTRIBUTE_SYSTEM.CREATION_DATE]: -111,
	[ATTRIBUTE_SYSTEM.IMPORT_DATE]: -112,
	[ATTRIBUTE_SYSTEM.MOD_USER]: -113,
	[ATTRIBUTE_SYSTEM.WIDTH]: -114,
	[ATTRIBUTE_SYSTEM.HEIGHT]: -115,
	[ATTRIBUTE_SYSTEM.CHECKSUM]: -117,
	[ATTRIBUTE_SYSTEM.THUMB_PATH]: -118,
	[ATTRIBUTE_SYSTEM.SMART_LABELS]: -119,
	[ATTRIBUTE_SYSTEM.DIMENSIONS]: -120,
	[ATTRIBUTE_SYSTEM.IDENTIFIED_PEOPLE]: -122,
};
